import { twMerge } from 'tailwind-merge';
import { sortByCreatedAt } from '@witmetrics/utils/arrays';
import {
  useFile,
  useFileCollectionsFromParentId,
  useFilesFromFileCollectionId,
} from '@witmetrics/data-store';
import Tooltip from '@/components/Tooltip';
import IconButton from '@/components/IconButton';
import { ArrowLeftIcon, FolderIcon } from '@/icons';
import type { FileCollectionPath } from '@/components/FileCollectionCrumbs';
import { ItemImage } from '@/components/FileCollection';
import { classes } from './utils';

export type CondensedFilesListProps = {
  activeCollectionId: number;
  baseCollectionId: number;
  activeFileId?: number;
  crumbs: FileCollectionPath[];
  onSelectFile: (fileId: number) => void;
  onSelectCollection: (collectionId: number) => void;
};

export default function CondensedFilesList({
  activeCollectionId,
  baseCollectionId,
  activeFileId,
  crumbs,
  onSelectFile,
  onSelectCollection,
}: CondensedFilesListProps) {
  const collections = useFileCollectionsFromParentId(activeCollectionId);
  const files = useFilesFromFileCollectionId(activeCollectionId);
  const canGoUp = activeCollectionId !== baseCollectionId;
  // eslint-disable-next-line witmetrics/no-multiline-comments
  /* Last crumb is the current folder, so the second-to-last is the parent.
    Falls back to baseCollectionId during transient states (e.g. before fetch
    populates crumbs) so the back button never points at undefined.
   */
  const parentCollectionId = crumbs[crumbs.length - 2]?.id ?? baseCollectionId;

  return (
    <div className={classes.list}>
      {canGoUp && (
        <div className={classes.upButton}>
          <IconButton
            title="Back"
            titlePosition="right"
            size="small"
            onClick={() => onSelectCollection(parentCollectionId)}>
            <ArrowLeftIcon />
          </IconButton>
        </div>
      )}
      {collections.map((c) => (
        <Tooltip
          key={`folder-${c.id}`}
          arrow
          enterDelay={0}
          placement="right"
          title={c.name}>
          <button
            className={classes.itemButton}
            type="button"
            onClick={() => onSelectCollection(c.id)}>
            <FolderIcon className={classes.folderIcon} />
          </button>
        </Tooltip>
      ))}
      {sortByCreatedAt(files).map((f) => (
        <CondensedFileItem
          key={`file-${f.id}`}
          active={activeFileId === f.id}
          fileId={f.id}
          onSelect={onSelectFile}
        />
      ))}
    </div>
  );
}

type CondensedFileItemProps = {
  fileId: number;
  active?: boolean;
  onSelect: (fileId: number) => void;
};

function CondensedFileItem({
  fileId,
  active,
  onSelect,
}: CondensedFileItemProps) {
  const file = useFile(fileId);
  if (!file) return null;
  return (
    <Tooltip arrow enterDelay={0} placement="right" title={file.name}>
      <button
        className={twMerge(classes.itemButton, active && classes.itemActive)}
        type="button"
        onClick={() => onSelect(fileId)}>
        <div className={classes.thumbWrapper}>
          <ItemImage fileId={fileId} />
        </div>
      </button>
    </Tooltip>
  );
}
